import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Clock, Crown, Loader2, MapPin, Ticket, Users } from "lucide-react";
import { toast } from "sonner";

import { guestRewardsApi } from "@/api/guest/guestRewardsApi.js";
import GuestLayout from "@/components/guest/GuestLayout.jsx";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useSession } from "@/hooks/useSession.js";
import {
  formatGuestDate,
  formatGuestDateTime,
  formatMembershipStatus,
  formatMoney,
  getApiData,
  getApiMessage,
  read,
} from "@/utils/guestRewardsFormat.js";
import {
  FullScreenLoading,
  GuestPageShell,
  LoadingBlock,
  PageHeader,
  RegistrationPrompt,
} from "@/pages/guest/GuestProfilePage.jsx";

const GuestMembershipPage = () => {
  const navigate = useNavigate();
  const { session, hotel, room, loading: sessionLoading } = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchaseId, setPurchaseId] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancellingId, setCancellingId] = useState("");

  const loadMemberships = async () => {
    if (!session) return;
    try {
      setLoading(true);
      const response = await guestRewardsApi.getMemberships();
      setData(getApiData(response));
    } catch (error) {
      toast.error(getApiMessage(error, "Unable to load memberships."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionLoading && !session) navigate("/guest/expired", { replace: true });
  }, [sessionLoading, session, navigate]);

  useEffect(() => {
    loadMemberships();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const handlePurchase = async (packageId) => {
    if (read(data, "requiresRegistration", "RequiresRegistration", true)) {
      navigate(`/guest/register?returnTo=${encodeURIComponent("/guest/membership")}`);
      return;
    }

    try {
      setPurchaseId(packageId);
      await guestRewardsApi.purchaseMembership({ packageId });
      toast.success("Membership package purchased successfully.");
      await loadMemberships();
    } catch (error) {
      toast.error(getApiMessage(error, "Unable to buy membership package."));
    } finally {
      setPurchaseId("");
    }
  };

  const handleCancelPurchase = async () => {
    const id = read(cancelTarget, "id", "Id");
    if (!id) return;

    try {
      setCancellingId(id);
      await guestRewardsApi.cancelMembershipPurchase(id, { reason: cancelReason });
      toast.success("Membership package cancelled successfully.");
      setCancelTarget(null);
      setCancelReason("");
      await loadMemberships();
    } catch (error) {
      toast.error(getApiMessage(error, "Unable to cancel membership package."));
    } finally {
      setCancellingId("");
    }
  };

  if (sessionLoading) return <FullScreenLoading />;
  if (!session) return null;

  const packages = read(data, "packages", "Packages", []);
  const purchases = read(data, "purchases", "Purchases", []);
  const requiresRegistration = read(data, "requiresRegistration", "RequiresRegistration", true);
  const activePackageIds = new Set(
    purchases
      .filter((purchase) => isActivePurchase(purchase))
      .map((purchase) => read(purchase, "packageId", "PackageId"))
      .filter(Boolean)
  );

  return (
    <>
      <Helmet>
        <title>Membership - Guest Portal</title>
      </Helmet>
      <GuestLayout hotel={hotel} room={room} session={session}>
        <GuestPageShell onBack={() => navigate("/guest/home")}>
          <PageHeader icon={Crown} title="Memberships" subtitle="View hotel packages and included events." />
          {loading ? (
            <LoadingBlock text="Loading memberships..." />
          ) : (
            <div className="space-y-6">
              {requiresRegistration ? (
                <RegistrationPrompt onRegister={() => navigate(`/guest/register?returnTo=${encodeURIComponent("/guest/membership")}`)} />
              ) : null}

              {!requiresRegistration && (
                <section>
                  <h2 className="mb-3 text-lg font-bold text-slate-900">Active memberships</h2>
                  {purchases.length === 0 ? (
                    <EmptyState text="No active memberships yet." />
                  ) : (
                    <div className="space-y-3">
                      {purchases.map((purchase) => {
                        const eventCoupons = read(purchase, "eventCoupons", "EventCoupons", []);
                        return (
                        <div key={read(purchase, "id", "Id")} className="rounded-2xl border bg-white p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold text-slate-900">
                                {read(purchase, "packageName", "PackageName", "Membership package")}
                              </p>
                              <p className="mt-1 text-sm text-slate-500">
                                Valid {formatGuestDate(read(purchase, "validFrom", "ValidFrom"))} to{" "}
                                {formatGuestDate(read(purchase, "validTo", "ValidTo"))}
                              </p>
                            </div>
                            <span className="rounded-full border px-3 py-1 text-xs font-medium">
                              {formatMembershipStatus(read(purchase, "status", "Status"))}
                            </span>
                          </div>
                          {isCancelledPurchase(purchase) ? (
                            <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700">
                              Cancelled {formatGuestDateTime(read(purchase, "cancelledAtUtc", "CancelledAtUtc"))}
                              {read(purchase, "cancellationReason", "CancellationReason") ? `: ${read(purchase, "cancellationReason", "CancellationReason")}` : ""}
                            </p>
                          ) : null}
                          {eventCoupons.length > 0 && (
                            <div className="mt-4 space-y-2">
                              {eventCoupons.map((coupon) => {
                                const packageEvent = read(coupon, "packageEvent", "PackageEvent", {});
                                const eventImageUrl = read(packageEvent, "imageUrl", "ImageUrl", "");
                                return (
                                  <div
                                    key={read(coupon, "id", "Id")}
                                    className="overflow-hidden rounded-xl border border-[#e8d8c8] p-3"
                                    style={
                                      eventImageUrl
                                        ? {
                                            backgroundImage: `linear-gradient(rgba(255,250,243,0.28), rgba(255,250,243,0.48)), url("${eventImageUrl}")`,
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                          }
                                        : { backgroundColor: "#f8fafc" }
                                    }
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div>
                                        <p className="font-medium text-slate-900">{read(packageEvent, "title", "Title", "Package event")}</p>
                                        <p className="mt-1 text-xs text-slate-500">
                                          {read(packageEvent, "locationName", "LocationName", "Location not set")}
                                        </p>
                                      </div>
                                      <span className="rounded-full border bg-white px-2 py-1 text-xs">
                                        {formatEventCouponStatus(read(coupon, "status", "Status"))}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {canCancelPurchase(purchase) ? (
                            <Button
                              type="button"
                              variant="outline"
                              className="mt-4 w-full border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                              onClick={() => {
                                setCancelTarget(purchase);
                                setCancelReason("");
                              }}
                            >
                              Cancel membership
                            </Button>
                          ) : null}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}

              <section>
                <h2 className="mb-3 text-lg font-bold text-slate-900">Available packages</h2>
                {packages.length === 0 ? (
                  <EmptyState text="No membership packages available right now." />
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {packages.map((item) => (
                      <PackageCard
                        key={read(item, "id", "Id")}
                        item={item}
                        owned={activePackageIds.has(read(item, "id", "Id"))}
                        loading={purchaseId === read(item, "id", "Id")}
                        onPurchase={() => handlePurchase(read(item, "id", "Id"))}
                      />
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}
        </GuestPageShell>
      </GuestLayout>
      <Dialog open={Boolean(cancelTarget)} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel membership</DialogTitle>
            <DialogDescription>
              This cancels the purchased package and disables any unused coupon QR codes. It does not process a refund automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-900">
              {read(cancelTarget, "packageName", "PackageName", "Membership package")}
            </p>
            <Textarea
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              placeholder="Reason for cancellation"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCancelTarget(null)} disabled={Boolean(cancellingId)}>
              Keep membership
            </Button>
            <Button
              type="button"
              className="bg-red-700 text-white hover:bg-red-800"
              onClick={handleCancelPurchase}
              disabled={Boolean(cancellingId)}
            >
              {cancellingId ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Cancel membership
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const PackageCard = ({ item, owned, loading, onPurchase }) => {
  const events = read(item, "events", "Events", []).filter((packageEvent) => read(packageEvent, "isActive", "IsActive", true));
  return (
    <article className="flex flex-col rounded-2xl border bg-white p-4">
      <p className="text-lg font-bold text-slate-900">{read(item, "name", "Name", "Membership package")}</p>
      <p className="mt-2 text-sm text-slate-600">{read(item, "description", "Description", "Hotel membership package")}</p>
      <p className="mt-3 text-xl font-bold text-[#923536]">
        {formatMoney(read(item, "price", "Price", 0), read(item, "currency", "Currency", "NPR"))}
      </p>
      <div className="mt-4 flex-1 space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Included events</p>
        {events.length === 0 ? (
          <p className="rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">No package events linked yet.</p>
        ) : (
          events.map((packageEvent) => (
            <EventDetailCard key={read(packageEvent, "id", "Id")} packageEvent={packageEvent} />
          ))
        )}
      </div>
      {owned ? (
        <Button type="button" disabled variant="outline" className="mt-4">
          Active membership
        </Button>
      ) : (
        <Button type="button" onClick={onPurchase} disabled={loading} className="mt-4 bg-[#923536] text-white hover:bg-[#7f2d2e]">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Buy package
        </Button>
      )}
    </article>
  );
};

const EventDetailCard = ({ packageEvent }) => {
  const eventImageUrl = read(packageEvent, "imageUrl", "ImageUrl", "");
  const description = read(packageEvent, "description", "Description", "");
  const location = read(packageEvent, "locationName", "LocationName", "");
  const duration = read(packageEvent, "durationText", "DurationText", "");
  const people = read(packageEvent, "quantityIncluded", "QuantityIncluded", "");
  const usageLimit = read(packageEvent, "usageLimit", "UsageLimit", "");
  const startDate = read(packageEvent, "startDate", "StartDate", "");
  const endDate = read(packageEvent, "endDate", "EndDate", "");
  const dateRange = formatEventDateRange(startDate, endDate);

  return (
    <div
      className="overflow-hidden rounded-xl border border-[#923536]/15 p-3"
      style={
        eventImageUrl
          ? {
              backgroundImage: `linear-gradient(rgba(255,250,243,0.28), rgba(255,250,243,0.48)), url("${eventImageUrl}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : { backgroundColor: "rgba(146, 53, 54, 0.05)" }
      }
    >
      <div className="flex items-start gap-2">
        <Ticket className="mt-0.5 h-4 w-4 shrink-0 text-[#923536]" />
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">{read(packageEvent, "title", "Title", "Package event")}</p>
          {description ? <p className="mt-1 text-xs leading-5 text-slate-600">{description}</p> : null}
        </div>
      </div>
      <div className="mt-3 grid gap-2 text-xs text-slate-600">
        {location ? <EventMeta icon={MapPin} text={location} /> : null}
        {duration ? <EventMeta icon={Clock} text={duration} /> : null}
        {people ? <EventMeta icon={Users} text={`${people} people included`} /> : null}
        {usageLimit ? <EventMeta icon={Ticket} text={`${usageLimit} redemption${Number(usageLimit) === 1 ? "" : "s"}`} /> : null}
        {dateRange ? <EventMeta icon={CalendarDays} text={dateRange} /> : null}
      </div>
    </div>
  );
};

const EventMeta = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2">
    <Icon className="h-3.5 w-3.5 shrink-0 text-[#923536]" />
    <span>{text}</span>
  </div>
);

const isActivePurchase = (purchase) => {
  const status = read(purchase, "status", "Status");
  const validTo = read(purchase, "validTo", "ValidTo");
  const statusActive = Number(status) === 1 || String(status).toLowerCase() === "active";
  if (!statusActive || !validTo) return statusActive;
  const expiry = new Date(validTo);
  return Number.isNaN(expiry.getTime()) || expiry >= new Date();
};

const canCancelPurchase = (purchase) => {
  const status = read(purchase, "status", "Status");
  const normalized = Number(status);
  const label = String(status || "").toLowerCase();
  return normalized === 1 || label === "active";
};

const isCancelledPurchase = (purchase) => {
  const status = read(purchase, "status", "Status");
  return Number(status) === 3 || String(status || "").toLowerCase() === "cancelled";
};

const formatEventCouponStatus = (value) => {
  const normalized = Number(value);
  if (normalized === 2) return "Pending approval";
  if (normalized === 3) return "Approved";
  if (normalized === 4) return "Used";
  if (normalized === 5) return "Expired";
  if (normalized === 6) return "Cancelled";
  return "Available";
};

const formatEventDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) return "";
  const start = formatGuestDate(startDate);
  const end = formatGuestDate(endDate);
  if (startDate && endDate) return `${start} to ${end}`;
  return startDate ? start : end;
};

const EmptyState = ({ text }) => (
  <div className="rounded-2xl border border-dashed p-6 text-center text-sm text-slate-600">{text}</div>
);

export default GuestMembershipPage;
